import { Form, Button, Input, Spin, Typography, Radio, RadioChangeEvent, Select, Tabs, Divider } from "antd";
import { ChangeEvent, useContext, useEffect, useState } from "react";
import Account from "../../../models/Account";
import { ToastContext } from "../../../context/ToastContext";
import AccountService from "../../../services/crud/AccountService";
import { LoadingOutlined } from "@ant-design/icons/lib/icons";
import { StructuredEror } from "../../../helpers/structured-error";
import lodash from "lodash";
import { amountKronaToCents, formatPointsInCents } from "../../../utils/common";
import { ActorType } from "../../../models/ActorType";
import BookingExternalService from "../../../services/crud/BookingExternalService";
import TspService from "../../../services/crud/TspService";
import Tsp from "../../../models/Tsp";
import UserService from "../../../services/crud/UserService";
import { LangKey } from "../../../models/LangKey";
import { EntitySelector } from "../../../components/forms/selects/EntitySelector";
import { capitalizeFirstLetterLowerRest } from "../../../helpers/string";
import { WalletActor, WalletTransaction } from "../../../models/Wallet";
import WalletService from "../../../services/crud/WalletService";

enum ActorMode {
  account,
  tsp,
  system,
}

interface WalletFormParams {
  /// User which owns the wallet.
  userId: number;
  /// Set this when actor is a company or real_estate account.
  actorAccountId?: number;
  /// Set this when actor is TSP of given booking.
  actorBookingsExternalId?: number;
  form: any;

  onBalanceUpdate?: (newBalanceCents: number) => void;
}

/**
 * Form which displays the current Wallet balance and provides ability for
 * user to create a transaction to alter the balance of wallet.
 *
 * Parameter to identify the wallet owner:
 * - [userId]
 *
 * Optional parameters to pre-select actor (and lock down the form to only this actor):
 * - [actorAccountId]
 * - [actorBookingsExternalId]
 *
 * Events:
 * - [onBalanceUpdate] - is called when balance change has been confirmed by the backend
 *
 * @param props
 * @returns
 */
export const WalletForm = (props: WalletFormParams) => {
  const { loading, loadMessage, showMessage } = useContext(ToastContext);

  const [actorAccountId, setActorAccountId] = useState<number|null>(props.actorAccountId ?? null);
  const [actorAccount, setActorAccount] = useState<Account|null>(null);
  const [actorTsp, setActorTsp] = useState<Tsp|null>(null);
  const {userId, actorBookingsExternalId, form} = props;
  const actorModeLocked:boolean = !!(actorBookingsExternalId || props.actorAccountId);

  const [userLangKey, setUserLangKey] = useState<LangKey>(LangKey.EN);
  const [privateAccount, setPrivateAccount] = useState<Account|null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [changeBalanceBy, setChangeBalanceBy] = useState<number>();
  const [message, setMessage] = useState<string>('');
  const [actorName, setActorName] = useState<string>('');
  const [actorType, setActorType] = useState<ActorType>(ActorType.REFUND);
  const [actorTspId, setActorTspId] = useState<number|null>(null);
  const [actorMode, setActorMode] = useState<ActorMode>(
    actorAccountId
    ? ActorMode.account
    : (actorBookingsExternalId ? ActorMode.tsp : null)
  )
  const [balanceOperation, setBalanceOperation] = useState<string>('add'); // 'add' or 'deduct'
  const [balancePreview, setBalancePreview] = useState<string>();
  const [typeOptions, setTypeOptions] = useState([
    { label: "Compensation", value: ActorType.COMPENSATION },
    { label: "Refund", value: ActorType.REFUND },
    { label: "System", value: ActorType.SYSTEM },
  ]);
  const initialChangeBalanceBy = 0.0;

  useEffect(() => {
    form.resetFields();
    setChangeBalanceBy(initialChangeBalanceBy);

    // Initial loading
    loadMessage("Loading...", async () => {
      let changeSek = initialChangeBalanceBy;

      const booking = actorBookingsExternalId ? await BookingExternalService.read(actorBookingsExternalId) : null;
      const actorAccount = actorAccountId ? await AccountService.getAccount(actorAccountId) : null;
      const [tsp, privateAccount, user] = await Promise.all([
        booking ? TspService.getTsp(booking.tspId) : Promise.resolve<Tsp|null>(null),
        AccountService.getUserPrivateAccount(userId),
        UserService.getUser(userId),
      ])

      form.setFieldValue('name', user?.username ?? '');
      const langKey = user.langKey ?? LangKey.EN;

      if (actorAccount) {
        if ("error" in actorAccount) {
          console.log(actorAccount as unknown as StructuredEror);
          setErrorMessage((actorAccount as unknown as StructuredEror).error);
        } else {
          setActorName(actorAccount.name ?? '');

          setActorType(actorAccount.type as ActorType ?? ActorType.REFUND);
          setActorTspId(null);
          setActorAccount(actorAccount);
          setMessageForAccount(langKey);
        }
      } else if (booking) {
        changeSek = booking.totalPriceInKr;
        setActorTspId(booking.tspId);
        setActorTsp(tsp);
        setActorName(tsp?.name);
        setActorType(ActorType.REFUND);
        setMessageForBooking(actorBookingsExternalId, langKey);
      }

      if ("error" in privateAccount) {
        console.log(privateAccount as unknown as StructuredEror);
        setErrorMessage((privateAccount as unknown as StructuredEror).error);
      } else {
        setPrivateAccount(privateAccount);
        form.setFieldsValue({
          balancePoints: formatPointsInCents(privateAccount.balanceCents) ?? '',
        });
        setBalancePreview(formatPointsInCents(privateAccount.balanceCents + amountKronaToCents(changeSek)));
      }

      setChangeBalanceBy(changeSek);
      setUserLangKey(langKey);
    }).finally(() => {
      return "Loaded account";
    });
  }, []);

  // Updates typeOptions
  useEffect(() => {
    let actorTypes = [];
    let defaultActorType = ActorType.SYSTEM;
    if (actorMode === ActorMode.account) {
      actorTypes = [
        ActorType.SYSTEM,
      ];
      if (actorAccount?.type == "real_estate") {
        actorTypes.push(ActorType.REAL_ESTATE);
        defaultActorType = ActorType.REAL_ESTATE;
      } else if (actorAccount?.type == "company") {
        actorTypes.push(ActorType.COMPANY);
        defaultActorType = ActorType.COMPANY;
      }
    } else if (actorMode === ActorMode.tsp) {
      actorTypes = [
        ActorType.CAMPAIGN,
        ActorType.COMPENSATION,
        ActorType.REFUND,
        ActorType.KICKBACK,
        ActorType.SYSTEM,
      ];
      defaultActorType = ActorType.REFUND;
    } else if (actorMode == ActorMode.system) {
      actorTypes = [
        ActorType.BOOKINGS_EXTERNAL,
        ActorType.CAMPAIGN,
        ActorType.COMPANY,
        ActorType.COMPENSATION,
        ActorType.KICKBACK,
        ActorType.REAL_ESTATE,
        ActorType.REFUND,
        ActorType.SYSTEM,
        ActorType.USER,
      ];
      defaultActorType = ActorType.SYSTEM;
    } else if (actorMode) {
      throw Error('Unknown ActorMode');
    }
    actorTypes.sort();
    const options = actorTypes.map((at) => ({ label: capitalizeFirstLetterLowerRest(at.toString()).replaceAll('_', ' '), value: at }));
    setTypeOptions(options);
    if (actorTypes.indexOf(actorType) === -1 ||
      // Only had system, and now added company/real estate?
      actorMode == ActorMode.account && defaultActorType != ActorType.SYSTEM && typeOptions.length <= 1
    ) {
      setActorType(defaultActorType);
    }
  }, [actorMode, actorAccount, actorTsp]);

  // Updates message
  useEffect(() => {
    if (message || actorModeLocked) {
      return;
    }
    if (actorMode === ActorMode.account) {
      setMessageForAccount(userLangKey);
    } else if (actorMode === ActorMode.tsp) {
      setMessageForBooking(actorBookingsExternalId, userLangKey);
    }
  }, [actorMode, actorAccount, actorTsp, userLangKey]);

  const onActorModeChanged = (e: RadioChangeEvent) => {
    const newActorMode = e.target.value as ActorMode;
    if (newActorMode != actorMode) {
      setActorMode(newActorMode);
      if (newActorMode !== ActorMode.system) {
        setActorName('');
        setActorAccountId(null);
        setActorTspId(null);
      }
    }
  }

  const onOperationModeChanged = (e: RadioChangeEvent) => {
    setBalanceOperation(e.target.value);
    if (privateAccount) {
      const valueCents = amountKronaToCents(changeBalanceBy);
      setBalancePreview(
        formatPointsInCents(privateAccount.balanceCents + (e.target.value === 'add' ? valueCents : -valueCents)));
    }
  };

  const onSelectAccount = async (accountId: number, account: Account) => {
    setActorAccountId(accountId);
    setActorAccount(account);
    setActorName(account.name);
  }

  const onSelectTsp = async (tspId: number, tsp: Tsp) => {
    setActorTspId(tspId);
    setActorTsp(tsp);
    setActorName(tsp.name);
  }

  /// for SuperUser mode
  const onActorAccountIdChanged = async (event: ChangeEvent<HTMLInputElement>) => {
    const valueStr = (event.target as any).value.trim();
    const value = valueStr !== '' ? parseInt(valueStr) : null;
    setActorAccountId(value);
  }
  /// for SuperUser mode
  const onActorTspIdChanged = async (event: ChangeEvent<HTMLInputElement>) => {
    const valueStr = (event.target as any).value.trim();
    const value = valueStr !== '' ? parseInt(valueStr) : null;
    setActorTspId(value);
  }

  const setMessageForAccount = (langKey: LangKey) => {
    setMessage(langKey == LangKey.SV ? 'Mobilitetspott' : 'Mobility budget');
  }
  const setMessageForBooking = (bookingId: number|undefined|null, langKey: LangKey) => {
    setMessage(
      langKey == LangKey.SV
        ? `Återbetalning bokning ${actorBookingsExternalId ?? ''}`.trim()
        : `Refund booking ${actorBookingsExternalId ?? ''}`.trim()
    );
  }

  const onChangeByChanged = (sValue: string): void => {
    if (!privateAccount) return;
    const value = parseFloat(sValue);
    setChangeBalanceBy(value);
    const valueCents = amountKronaToCents(value);
    setBalancePreview(
      formatPointsInCents(privateAccount.balanceCents + (balanceOperation === 'add' ? valueCents : -valueCents)));
  };

  const doBalanceOperation = () => {
    if (!privateAccount) return;
    const operation = balanceOperation == 'add' ? 'add' : 'withdraw';
    const payload = new WalletTransaction({
      ownerAccountId: privateAccount.id,
      operation: operation,
      amountCents: amountKronaToCents(changeBalanceBy),
      message: message?.trim(),
      actor: new WalletActor({
        name: actorName?.trim(),
        type: actorType,
        accountId: actorAccountId,
        tspId: actorTspId,
      }),
    });
    if (payload.amountCents === 0) {
      showMessage('Amount is zero', "error");
      return;
    }
    const required = [
      payload.operation,
      payload.amountCents,
      payload.actor.name,
      payload.actor.type,
    ];
    const allowed = required.map((value) => value !== null && value !== undefined)
      && payload.actor.name.length > 0;
    if (!allowed) {
      showMessage('Not all required fields given', "error");
      return;
    }
    if ((payload.actor.accountId !== null && payload.actor.accountId !== undefined)
      === (payload.actor.tspId !== null && payload.actor.tspId !== undefined)
    ) {
      showMessage('Both Actor TSP and Actor Account cannot be given at the same time', "error");
      return;
    }

    performBalanceChange(payload);
  };
  const performBalanceChange = async (payload: WalletTransaction) => {
    await loadMessage("Altering balance...", async () => {
      const tId = await WalletService.createWalletTransaction(payload);
      console.log(tId);
      const updatedAccount = await AccountService.getAccount(privateAccount.id);
      setPrivateAccount(updatedAccount)
      setChangeBalanceBy(0);
      setMessage("");
      form.setFieldsValue({
        balancePoints: formatPointsInCents(updatedAccount.balanceCents) ?? '',
      });
      setBalancePreview(formatPointsInCents(updatedAccount.balanceCents) ?? '');
      if (props.onBalanceUpdate) {
        props.onBalanceUpdate(updatedAccount.balanceCents);
      }
      showMessage("Balance altered successfully", "success");
    });
  };
  const isMobile = window.matchMedia("(max-width: 600px)").matches;

  const content = (
    <Form form={form}>
      {errorMessage && (
        <div>
          <Typography.Title level={4}>{errorMessage}</Typography.Title>
          <Typography.Link href="/users">Go to Users to resolve user properties</Typography.Link>
        </div>
      )}
      {!errorMessage && (
        <>
          <Form.Item noStyle name="id">
            <Input type="hidden" />
          </Form.Item>
          <Form.Item name="name" label="Wallet owner" rules={[{ required: false }]}>
            <Input disabled type="text" />
          </Form.Item>
          <Form.Item name="balancePoints" label="Current balance" rules={[{ required: false }]}>
            <Input disabled type="text" />
          </Form.Item>
          <Form.Item label="Alter balance" rules={[{ required: true }]}>
            <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
              <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 100, marginRight: 10, marginBottom: 0}}>
                Operation
              </p>
              <Radio.Group
                buttonStyle="solid"
                defaultValue={balanceOperation}
                style={{ display: 'block', flex: 1 }}
                onChange={onOperationModeChanged}
              >
                <Radio className="danger" value={"deduct"}>Deduct</Radio><br/>
                <Radio value={"add"}>
                  Add (payout)
                </Radio><br/>
              </Radio.Group>
            </div>
            <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
              <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                Amount
              </p>
              <Input
                type="number"
                onChange={(e) => onChangeByChanged((e.target as HTMLInputElement).value)}
                name="changeBy"
                value={changeBalanceBy}
                style={{flex: 1}}
                min="0"
              />
            </div>

            <Divider />

            { !actorModeLocked && (
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 100, marginRight: 10, marginBottom: 0}}>
                  Who is the { balanceOperation === 'add' ? 'payout' : 'deduct' } actor?
                </p>
                <Radio.Group
                  buttonStyle="solid"
                  defaultValue={actorMode}
                  style={{ display: 'block', flex: 1 }}
                  onChange={onActorModeChanged}
                  disabled={actorModeLocked}
                >
                  <Radio value={ActorMode.account}>Company or Real estate</Radio><br/>
                  <Radio value={ActorMode.tsp}>TSP</Radio><br/>
                  <Radio value={ActorMode.system}>Super user</Radio><br/>
                </Radio.Group>
              </div>
            )}
            { actorMode == ActorMode.account && (
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                  Account
                </p>
                { userId && (<EntitySelector.UserAccounts
                    userId={userId}
                    defaultValue={actorAccountId}
                    onSelectAccount={onSelectAccount}
                    accountTypes={['company', 'real_estate']}
                    disabled={actorModeLocked}
                />)}
              </div>
            )}
            { actorMode == ActorMode.tsp && (
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                  TSP
                </p>
                <EntitySelector.Tsps
                    defaultValue={actorTspId}
                    onSelectTsp={onSelectTsp}
                    disabled={actorModeLocked}
                />
              </div>
            )}
            { actorMode == ActorMode.system && (<>
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                  Actor TSP id {actorTspId}
                </p>
                <Input type="text"
                    value={actorTspId}
                    onChange={onActorTspIdChanged}
                />
              </div>
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                  Actor account id {actorAccountId}
                </p>
                <Input type="text"
                    value={actorAccountId}
                    onChange={onActorAccountIdChanged}
                />
              </div>
            </>)}
            { actorMode !== undefined && actorMode !== null && (<>
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                  Name
                </p>
                <Input type="text" value={actorName} onChange={(e) => setActorName((e.target as HTMLInputElement).value)}
                  disabled={actorMode !== ActorMode.system} />
              </div>
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                  Type
                </p>
                <div style={{flex: 1}}>
                  <Select<ActorType>
                      style={{width: '100%'}}
                      options={typeOptions}
                      value={actorType}
                      onSelect={(value) => setActorType(value)}
                      aria-required
                    ></Select>
                  </div>
              </div>
              <div style={{display: isMobile ? 'block' : 'flex', marginBottom: 15, alignItems: 'center'}}>
                <p style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 140, marginRight: 10, marginBottom: 0}}>
                  Message ({userLangKey.toLocaleUpperCase()})
                </p>
                <Input type="text" value={message} onChange={(e) => setMessage((e.target as HTMLInputElement).value)} />
              </div>

              <Divider />

              <div style={{display: isMobile ? 'block' : 'flex'}}>
                <div style={{display: 'block', flex: 0, minWidth: 140, maxWidth: 100, marginRight: 10}}></div>
                <Button disabled={loading} type="primary" onClick={doBalanceOperation} style={{ display: 'block', flex: 0 }}>
                  {lodash.capitalize(balanceOperation)} {changeBalanceBy} p
                </Button>
              </div>
              <Typography.Text style={{ right: 0 }}>
                <br/>Balance after operation: {balancePreview}
              </Typography.Text>
              <Typography.Text style={{ right: 0 }}>
                <br/>1 p = 1 kr
              </Typography.Text>
              <Typography.Text style={{ right: 0 }}>
                <br/>
                <br/>All changes of balance are logged as transactions attributed to your admin user id.
              </Typography.Text>
            </>)}
          </Form.Item>
        </>
      )}
    </Form>
  );

  if (loading) {
    return (
      <Spin size="large" tip="Loading" indicator={<LoadingOutlined />}>
        {content}
      </Spin>
    );
  }
  return content;
};
